import { useEffect, useState } from "react";
import { ICoord } from "../../../@types/GoogleMaps/ICoord";
import { Marker, useMap } from "react-leaflet";
import L from "leaflet";
import { renderToStaticMarkup } from "react-dom/server";
import { ReactComponent as CattleIcon } from "../../../assets/svg/icons/animais.svg";

export type ICustomBatch = {
  initials: string;
  coords: ICoord;
  id: string;
  farmId: string;
  areaId: string;
  visible: boolean;
  currentMicroArea?: number;
  background?: string;
};

type BatchesIconsProps = {
  batches: ICustomBatches;
};

interface ICustomBatches extends Array<ICustomBatch> { }

export function BatchesIcons({ batches }: BatchesIconsProps) {
  const map = useMap();
  const [clusters, setClusters] = useState<Array<any>>([]);

  const createCattleHtml = (background: string | undefined) => {
    const svg = renderToStaticMarkup(<CattleIcon fill="#4D4D4D" />);
    return `<div class=\"labelbatches__container\" style=\"background:${background || "#FFF"};border:3px solid #FFF\">${svg}</div>`;
  };

  const createClusterHtml = (count: number) => {
    const svg = renderToStaticMarkup(<CattleIcon fill="#ffffff" />);
    return `
      <div class=\"batch-cluster\">
        <div class=\"cluster-icon\">${svg}</div>
        <div class=\"cluster-count\">${count}</div>
      </div>
    `;
  };

  useEffect(() => {
    if (!map) return;

    const computeClusters = () => {
      const pxThreshold = Math.round(48 * Math.pow(1.1, 2)); // cluster radius in pixels
      const visible = (batches || []).filter(b => b.visible && b.coords && typeof b.coords.lat === 'number');

      const points = visible.map(b => ({
        batch: b,
        point: map.latLngToLayerPoint([b.coords.lat, b.coords.lng])
      }));

      const used = new Array(points.length).fill(false);
      const out: Array<any> = [];

      for (let i = 0; i < points.length; i++) {
        if (used[i]) continue;
        const group = [points[i]];
        used[i] = true;
        for (let j = i + 1; j < points.length; j++) {
          if (used[j]) continue;
          if (points[i].point.distanceTo(points[j].point) <= pxThreshold) {
            group.push(points[j]);
            used[j] = true;
          }
        }

        const lat = group.reduce((s, p) => s + p.batch.coords.lat, 0) / group.length;
        const lng = group.reduce((s, p) => s + p.batch.coords.lng, 0) / group.length;

        out.push({ coords: { lat, lng }, items: group.map(g => g.batch), count: group.length });
      }

      setClusters(out);
    };

    computeClusters();
    map.on('moveend zoomend resize', computeClusters);
    return () => {
      map.off('moveend zoomend resize', computeClusters);
    };
  }, [map, batches]);

  return (
    <>
      {clusters.map((c, idx) => {
        if (c.count === 1) {
          const icon = L.divIcon({ html: createCattleHtml(c.items[0].background), className: 'batch-marker', iconSize: [52, 52] });
          return <Marker key={`b-single-${idx}`} position={[c.coords.lat, c.coords.lng]} icon={icon} />;
        }

        const icon = L.divIcon({ html: createClusterHtml(c.count), className: 'batch-cluster-wrapper', iconSize: [40, 40] });
        return (
          <Marker
            key={`b-cluster-${idx}`}
            position={[c.coords.lat, c.coords.lng]}
            icon={icon}
            eventHandlers={{
              click: () => {
                try {
                  const bounds = L.latLngBounds(c.items.map((it: ICustomBatch) => [it.coords.lat, it.coords.lng]));
                  map.fitBounds(bounds.pad(0.5));
                } catch (e) {
                  // noop
                }
              }
            }}
          />
        );
      })}
    </>
  );
}
